/*
 * Copyright (c) 2025 - 2026. Bluemate B.V.
 */
import './style/reviewComponent.scss';

import React, {useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import ProductReview from '../../models/productReviewModel';

import BolRating from '../bolRating/bolRating';

import RouterLink from '../controls/link/routerLink';

import Image from '../misc/image';

import IDate from '@dvrd/idate';

import {AppContext} from '../misc/appContext';

import {LAST_REVIEW_FLEX_KEY} from '../../util/constants';

import {AwesomeIcon, stopPropagation} from '@dvrd/dvr-controls';

import classNames from 'classnames';

interface Props {
    review: ProductReview;
    withLinks?: boolean;
    className?: string;
}

export default function ReviewComponent(props: Props) {
    const {t} = useTranslation();
    const {account} = useContext(AppContext).accountContext;
    const {review, withLinks, className} = props;
    const [dayText, isNew] = useMemo(() => {
        const lastSeen = account?.getFlex(LAST_REVIEW_FLEX_KEY);
        const today = IDate.now();
        const reviewDate = review.reviewDate;
        const isNew = new IDate(lastSeen).isBefore(review.createdAtMoment);
        let dayText: string;
        const diffInDays = reviewDate.absDiff(today, 'days');
        if (reviewDate.isAt(today, 'day')) dayText = t('reviewAnalytics.reviewComponent.today', 'Vandaag');
        else if (diffInDays === 1) dayText = t('reviewAnalytics.reviewComponent.yesterday', 'Gisteren');
        else dayText = `${diffInDays} ${t('reviewAnalytics.reviewComponent.daysAgo', 'dagen geleden')}`;
        return [dayText, isNew];
        // Stable string deps: the reviewDate/createdAtMoment getters return a
        // FRESH IDate per read, so keying on them defeats the memo entirely.
    }, [account?.id, review.reviewDateString, review.createdAt, t]);

    function renderHeader() {
        return (
            <div className='review-header'>
                <div className='title-row'>
                    <BolRating value={review.rating / 20} />
                    <label className='review-title'>{review.reviewTitle}</label>
                    <div className='date-container'>
                        {isNew && (
                            <div className='new-tag'>
                                <AwesomeIcon name='eye' prefix='far' />
                                <span>{t('reviewAnalytics.reviewComponent.new', 'Nieuw')}</span>
                            </div>
                        )}
                        <label className='day-text'>{dayText}</label>
                    </div>
                </div>
                <div className='subtitle-row'>
                    {renderAuthor()}
                    {!!withLinks && (
                        <div className='product-links'>
                            <RouterLink
                                to={`/review-analytics/${review.ean}`}
                                title={t(
                                    'reviewAnalytics.reviewComponent.viewAllReviews',
                                    'Alle reviews van dit product bekijken en analyseren'
                                )}
                                onClick={stopPropagation}
                            >
                                <Image name='logo-no-text' alt='' className='logo-link' />
                            </RouterLink>
                            <RouterLink
                                to={review.bolProductPageUrl}
                                target='_blank'
                                title={t('reviewAnalytics.reviewComponent.viewOnBol', 'Bekijk product op bol')}
                                onClick={stopPropagation}
                            >
                                <Image name='bol-fav-32' alt='' className='logo-link' />
                            </RouterLink>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    function renderAuthor() {
        return <label className='subtitle'>{review.getAuthorLine(' | ')}</label>;
    }

    function renderProsCons() {
        if (!review.pros.length && !review.cons.length) return null;
        return (
            <div className='pros-cons-container'>
                {!!review.pros.length && <div className='pros-cons'>{review.pros.map(renderProCon(true))}</div>}
                {!!review.cons.length && <div className='pros-cons'>{review.cons.map(renderProCon(false))}</div>}
            </div>
        );
    }

    function renderProCon(isPro: boolean) {
        return function (proCon: string, index: number) {
            const icon = isPro ? 'plus' : 'minus';
            return (
                <div key={index} className='pro-con'>
                    <AwesomeIcon name={icon} className={classNames('pro-con-icon', icon)} />
                    <label>{proCon}</label>
                </div>
            );
        };
    }

    return (
        <div className={classNames('review-component', className)} id={`review-${review.id}`}>
            {renderHeader()}
            {renderProsCons()}
            <label className='review-body'>{review.body}</label>
        </div>
    );
}
